import { Paginator } from 'array-paginator';
import { defineStore } from 'pinia';
import { ResolveListForExportCallback } from 'src/stores/_infrastructure/ResolveListForExportCallback';
import { dedupByAggregateId } from 'src/stores/_infrastructure/dedupByAggregateId';
import {
	COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
	prepareQueryForCompleteListExport,
} from 'src/stores/_infrastructure/prepareQueryForCompleteListExport';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import {
	INBOX_GENERIC_ACTIVITY_LIST_SORT,
	INBOX_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
} from 'src/stores/inbox/localStorageKeys';
import { GetAllDesktopAccessUsersForTheUserQuery, InboxQuery } from 'src/stores/inbox/queries';
import createInboxState from 'src/stores/inbox/state';
import { TSMap } from 'typescript-map';

import { Activity, ActivityRunType } from 'src/models/activity';
import { DateRangeSelection } from 'src/models/common';
import { FilterBoxes, FilterUsers, FilterUsersRunType } from 'src/models/filter';
import {
	ActivityListTypeEnum,
	GenericActivityListPagination,
	GenericActivityListSort,
} from 'src/models/genericActivityList';
import { SerializedQuery } from 'src/models/query';
import { StatusEnum } from 'src/models/status/enums';

import {
	removeScheduledTasksFromCollectionWithLogic,
	removeTheAggregateIdsFromTheCollection,
	setTheStateForTheAggregateIds,
} from 'src/util/aggregate/AggregateUtilityFunctions';
import { createCompareFunction } from 'src/util/collection/customCompareFunction';
import { DEFAULT_LIST_INSTANCE_ID, deriveListStoreId, isDefaultInstance } from 'src/util/listInstance/listInstanceKeys';

import { GetAllDesktopAccessUsersForTheUserCallBack, InboxQueryCallBack } from './callbacks';
import {
	CompleteListExportFetchPayload,
	DeleteSelectedAggregateIdsPayload,
	ListFetchPayload,
	QueryStorePayload,
	RemoveScheduledTasksPayload,
	SetDateFilterPayload,
	SetFilteredBoxesPayload,
	SetFilteredUsersPayload,
	SetListPayload,
	SetListStatePayload,
	SetPaginationPagePayload,
	SetPaginationPayload,
	SetSortPayload,
	SetViewedUserPayload,
} from './interfaces';

const DEFAULT_ROWS_PER_PAGE = 100;
const DEFAULT_LIST_SORT: GenericActivityListSort = {
	direction: 'DESC',
	property: 'timestamp',
};

const createDefaultPaginationEntry = (): GenericActivityListPagination => ({
	page: 1,
	rowsPerPage: DEFAULT_ROWS_PER_PAGE,
});

const replaceActiveQueryStoreSubscription = <T>({
	matchesLogicalView,
	nextSerializedQuery,
	parseQuery,
	queryStore,
	subscriptions,
}: {
	matchesLogicalView: (serializedQuery: SerializedQuery) => boolean;
	nextSerializedQuery: SerializedQuery;
	parseQuery: (serializedQuery: SerializedQuery) => T;
	queryStore: ListFetchPayload['queryStore'];
	subscriptions: SerializedQuery[];
}): SerializedQuery[] => {
	const staleSubscriptions = subscriptions.filter(matchesLogicalView);

	staleSubscriptions.forEach((serializedQuery) => {
		void queryStore?.unsubscribeQuery(parseQuery(serializedQuery));
	});

	return [...subscriptions.filter((serializedQuery) => !matchesLogicalView(serializedQuery)), nextSerializedQuery];
};

const defineInboxStore = (id: string) =>
	defineStore(id, {
		actions: {
			addQueryStoreSubscription(serializedQuery: SerializedQuery): void {
				this.queryStoreSubscriptions.push(serializedQuery);
			},
			clearQueryStoreSubscriptions(): void {
				this.queryStoreSubscriptions = [];
			},
			clearSubscriptions(payload: QueryStorePayload): void {
				if (payload.queryStore !== undefined) {
					this.queryStoreSubscriptions.forEach((serializedQuery: SerializedQuery) => {
						switch (serializedQuery.type) {
							case 'InboxQuery':
								void payload.queryStore?.unsubscribeQuery(
									InboxQuery.fromJSON<InboxQuery>(serializedQuery.query)
								);
								break;
							default:
								break;
						}
					});
				}

				this.clearQueryStoreSubscriptions();
			},
			deleteSelectedAggregateIds(payload: DeleteSelectedAggregateIdsPayload): void {
				const inboxListCopy = removeTheAggregateIdsFromTheCollection(
					this.inboxListOpen as never[],
					payload.aggregateIds
				) as unknown as Activity[];

				if (inboxListCopy !== this.inboxListOpen) {
					this.rebuildOpenInboxListSlices(inboxListCopy);
				}
			},
			async fetchDesktopAccessUsers(payload: QueryStorePayload): Promise<void> {
				if (payload.queryStore === undefined || this.desktopAccessUsersFetching) {
					return;
				}

				this.desktopAccessUsersFetching = true;

				try {
					await payload.queryStore.query<Array<FilterUsers>>({
						callback: new GetAllDesktopAccessUsersForTheUserCallBack(this),
						createTime: new Date(),
						query: new GetAllDesktopAccessUsersForTheUserQuery(),
						responseType: 'json',
						runType: FilterUsersRunType,
					});
				} finally {
					this.desktopAccessUsersFetching = false;
				}
			},
			fetchFilterBoxes(): void {
				this.setFilterBoxesValue([]);
			},
			fetchFilterUsers(): void {
				this.setFilterUsersValue([]);
			},
			async fetchOpenInboxList(payload: ListFetchPayload): Promise<void> {
				if (payload.queryStore === undefined) {
					this.setOpenInboxList({
						page: 1,
						payload: [],
						totalCount: 0,
					});
					return;
				}

				this.registerUnsubscribeFetchOpenInboxList(payload);
				const fetchToken = useGenericActivityListStore().beginListFetch(this.$id);

				const sort = this.sortFor(ActivityListTypeEnum.OPEN);
				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const page = pagination.page;
				const query = new InboxQuery(pagination, sort, this.viewedUserId);
				const queryIdentifier = query.uniqueQueryIdentifier();

				this.setPaginationPageValue({ activityListType: ActivityListTypeEnum.OPEN, page });
				this.setActiveQueryIdentifier(ActivityListTypeEnum.OPEN, queryIdentifier);

				const callback = new InboxQueryCallBack(this, page, queryIdentifier);
				callback.bindFetchStateRelease(() => useGenericActivityListStore().endListFetch(fetchToken));
				await payload.queryStore.query<Activity[]>({
					callback,
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: ActivityRunType,
				});
			},
			async fetchTheCompleteListForExport(payload: CompleteListExportFetchPayload): Promise<Activity[] | null> {
				if (payload.queryStore === undefined) {
					return null;
				}

				const pagination: GenericActivityListPagination = {
					page: 1,
					rowsPerPage: this.inboxListOpenTotalCount + COMPLETE_LIST_EXPORT_LIMIT_HEADROOM,
				};
				const query = prepareQueryForCompleteListExport(
					new InboxQuery(pagination, this.sortFor(ActivityListTypeEnum.OPEN), this.viewedUserId)
				);

				let completeList: Activity[] | null = null;

				await payload.queryStore.query<Activity[]>({
					callback: new ResolveListForExportCallback<Activity>((list) => {
						completeList = list;
					}),
					createTime: new Date(),
					query,
					responseType: 'json',
					runType: ActivityRunType,
				});

				return completeList;
			},
			isLatestQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): boolean {
				return this.activeQueryIdentifierByListType.get(activityListType) === queryIdentifier;
			},
			rebuildOpenInboxListSlices(inboxListCopy: Activity[]): void {
				this.inboxListOpenSliced.clear();

				if (inboxListCopy.length === 0) {
					this.inboxListOpen = [];
					return;
				}

				const inboxListCopyPaginator = new Paginator(inboxListCopy, DEFAULT_ROWS_PER_PAGE, 1);

				while (inboxListCopyPaginator.current <= inboxListCopyPaginator.total) {
					this.setOpenInboxList({
						page: inboxListCopyPaginator.current,
						payload: inboxListCopyPaginator.page(inboxListCopyPaginator.current) as Activity[],
						totalCount: this.inboxListOpenTotalCount,
					});

					inboxListCopyPaginator.next();
				}
			},
			registerUnsubscribeFetchOpenInboxList(payload: ListFetchPayload): void {
				if (payload.queryStore === undefined) {
					return;
				}

				const pagination = this.paginationFor(ActivityListTypeEnum.OPEN);
				const query = new InboxQuery(pagination, this.sortFor(ActivityListTypeEnum.OPEN), this.viewedUserId);

				this.replaceInboxQueryStoreSubscription({
					query,
					queryStore: payload.queryStore,
				});
			},
			removeScheduledTasksFromInbox(payload: RemoveScheduledTasksPayload): void {
				const inboxListCopy = removeScheduledTasksFromCollectionWithLogic(
					this.inboxListOpen,
					payload.aggregateIds,
					payload.targetDate,
					'inbox'
				);

				if (inboxListCopy.length !== this.inboxListOpen.length) {
					this.rebuildOpenInboxListSlices(inboxListCopy);
				}
			},
			replaceInboxQueryStoreSubscription(payload: {
				query: InboxQuery;
				queryStore: ListFetchPayload['queryStore'];
			}): void {
				const nextSerializedQuery: SerializedQuery = {
					query: JSON.stringify(payload.query),
					type: 'InboxQuery',
				};

				this.queryStoreSubscriptions = replaceActiveQueryStoreSubscription({
					matchesLogicalView: (serializedQuery) => serializedQuery.type === nextSerializedQuery.type,
					nextSerializedQuery,
					parseQuery: (serializedQuery) => InboxQuery.fromJSON<InboxQuery>(serializedQuery.query),
					queryStore: payload.queryStore,
					subscriptions: this.queryStoreSubscriptions,
				});
			},
			reset(payload: QueryStorePayload): void {
				this.clearSubscriptions(payload);

				this.setDateFilterValue('');
				this.setFilteredUsersValue([]);
				this.setFilteredBoxIdsValue([]);
				this.desktopAccessUsers = [];
				this.desktopAccessUsersFetching = false;
				this.viewedUserId = null;
				this.inboxListOpenTotalCount = 0;
				this.inboxListOpen = [];
				this.inboxListOpenSliced.clear();
				this.activeQueryIdentifierByListType = new TSMap<ActivityListTypeEnum, string>();
				this.queryStoreSubscriptions = [];
				this.pagination = new TSMap<ActivityListTypeEnum, GenericActivityListPagination>([
					[ActivityListTypeEnum.ALL_MODULE_LISTS_AT_ONCE, { page: 1, rowsPerPage: DEFAULT_ROWS_PER_PAGE }],
					[ActivityListTypeEnum.OPEN, { page: 1, rowsPerPage: DEFAULT_ROWS_PER_PAGE }],
				]);
			},
			setActiveQueryIdentifier(activityListType: ActivityListTypeEnum, queryIdentifier: string): void {
				this.activeQueryIdentifierByListType.set(activityListType, queryIdentifier);
			},
			setDateFilterAction(payload: SetDateFilterPayload): void {
				this.setDateFilterValue(payload.dateFilter);
			},
			setDateFilterValue(payload: DateRangeSelection | string): void {
				this.dateFilter = payload || '';
			},
			setDesktopAccessUsers(users: Array<FilterUsers>): void {
				this.desktopAccessUsers = users;
			},
			setFilterBoxesValue(payload: Array<FilterBoxes>): void {
				this.filterBoxes = payload || [];
			},
			setFilterUsersValue(payload: Array<FilterUsers>): void {
				this.filterUsers = payload || [];
			},
			setFilteredBoxIdsValue(payload: Array<string>): void {
				this.filteredBoxIds = payload || [];
			},
			setFilteredBoxesAction(_payload: SetFilteredBoxesPayload): void {
				void _payload;
				this.setFilteredBoxIdsValue([]);
			},
			setFilteredUsersAction(_payload: SetFilteredUsersPayload): void {
				void _payload;
				this.setFilteredUsersValue([]);
			},
			setFilteredUsersValue(payload: Array<FilterUsers>): void {
				this.filteredUsers = payload || [];
			},
			setListLoadError(): void {
				const genericActivityListStore = useGenericActivityListStore();
				genericActivityListStore.setListLoadError();
			},
			setListViewModeLarge(viewModeLarge: boolean): void {
				if (this.$id === 'inbox') {
					localStorage.setItem(
						INBOX_LIST_GENERIC_ACTIVITY_LIST_VIEW_MODE_LARGE,
						JSON.stringify(viewModeLarge)
					);
				}
				this.setListViewModeLargeValue(viewModeLarge);
			},
			setListViewModeLargeValue(payload: boolean): void {
				this.listViewModeLarge = payload;
			},
			setOpenInboxList({ page, payload, totalCount }: SetListPayload): void {
				this.inboxListOpenTotalCount = totalCount;
				this.inboxListOpenSliced.set(page, payload);

				const openSort = this.sortFor(ActivityListTypeEnum.OPEN);

				this.inboxListOpen = dedupByAggregateId(
					[...this.inboxListOpenSliced.values()].flatMap((entry) => entry)
				).sort(createCompareFunction<Activity>(openSort.property as keyof Activity, openSort.direction));
			},
			setPaginationAction(payload: SetPaginationPayload): void {
				this.setPaginationValue(payload);
			},
			setPaginationPageAction(payload: SetPaginationPagePayload): void {
				this.setPaginationPageValue(payload);
			},
			setPaginationPageValue({ activityListType, page }: SetPaginationPagePayload): void {
				const pagination = this.paginationFor(activityListType);
				pagination.page = page;
				this.pagination.set(activityListType, pagination);
			},
			setPaginationValue({ activityListType, pagination }: SetPaginationPayload): void {
				this.pagination.set(activityListType, pagination);
			},
			setSortAction(payload: SetSortPayload): void {
				this.setSortValue(payload);
				if (this.$id === 'inbox') {
					localStorage.setItem(INBOX_GENERIC_ACTIVITY_LIST_SORT, JSON.stringify(this.sort.toJSON()));
				}
			},
			setSortValue({ activityListType, sort }: SetSortPayload): void {
				this.sort.set(activityListType, sort);
			},
			setTheInboxStateToReady(payload: SetListStatePayload): void {
				this.updateStateForAggregateIds(payload.aggregateIds, StatusEnum.READY);
			},
			setTheInboxStateToSeen(payload: SetListStatePayload): void {
				this.updateStateForAggregateIds(payload.aggregateIds, StatusEnum.SEEN);
			},
			setTheInboxStateToUnseen(payload: SetListStatePayload): void {
				this.updateStateForAggregateIds(payload.aggregateIds, StatusEnum.UNSEEN);
			},
			async setViewedUser(payload: SetViewedUserPayload): Promise<void> {
				if (this.viewedUserId === payload.userId) {
					return;
				}

				this.viewedUserId = payload.userId;
				this.inboxListOpen = [];
				this.inboxListOpenSliced.clear();
				this.inboxListOpenTotalCount = 0;
				this.setPaginationPageValue({
					activityListType: ActivityListTypeEnum.OPEN,
					page: 1,
				});

				await this.fetchOpenInboxList({ queryStore: payload.queryStore });
			},
			updateStateForAggregateIds(aggregateIds: string[], status: StatusEnum): void {
				let openCountDelta = 0;
				aggregateIds.forEach((aggregateId) => {
					const known = this.inboxListOpen.find((item) => item.aggregateId === aggregateId);
					if (!known) {
						return;
					}
					const wasReady = known.statusButton.status === StatusEnum.READY;
					const willBeReady = status === StatusEnum.READY;
					if (!wasReady && willBeReady) {
						openCountDelta -= 1;
					} else if (wasReady && !willBeReady) {
						openCountDelta += 1;
					}
				});
				if (openCountDelta !== 0) {
					this.inboxListOpenTotalCount = Math.max(0, this.inboxListOpenTotalCount + openCountDelta);
				}

				const inboxListCopy = setTheStateForTheAggregateIds(this.inboxListOpen, aggregateIds, status);

				if (inboxListCopy.length > 0) {
					this.rebuildOpenInboxListSlices(inboxListCopy);
				}
			},
		},
		getters: {
			paginationFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListPagination {
				return (activityListType: ActivityListTypeEnum): GenericActivityListPagination =>
					state.pagination.get(activityListType) ?? createDefaultPaginationEntry();
			},
			sortFor(state): (activityListType: ActivityListTypeEnum) => GenericActivityListSort {
				return (activityListType: ActivityListTypeEnum): GenericActivityListSort =>
					state.sort.get(activityListType) ?? { ...DEFAULT_LIST_SORT };
			},
		},
		state: createInboxState,
	});

export const useInboxStore = defineInboxStore('inbox');

const inboxInstanceDefinitions = new Map<string, ReturnType<typeof defineInboxStore>>();

export const createInboxStore = (instanceId: string = DEFAULT_LIST_INSTANCE_ID) => {
	if (isDefaultInstance(instanceId)) {
		return useInboxStore();
	}

	const storeId = deriveListStoreId('inbox', instanceId);
	let definition = inboxInstanceDefinitions.get(storeId);

	if (definition === undefined) {
		definition = defineInboxStore(storeId);
		inboxInstanceDefinitions.set(storeId, definition);
	}

	return definition();
};

export * from './interfaces';
export * from './types';
